import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { Layout } from '@/components/layout';
import IconCard from '@/assets/icons/heroIcons/Card-front.svg';
import IconThird from '@/assets/icons/heroIcons/Icon-third.svg';
import IconMaxi from '@/assets/icons/heroIcons/Maxi.svg';
import MoreInfoSection from '@/sections/MoreInfo';
import { SectionDelimiter } from '@/components/SectionDelimiter';
import { getIntlProps, type IntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { Trans, useTranslation } from 'react-i18next';
import { LINKS } from '@/constants/links';
import React, { useEffect, useRef, useState } from 'react';
import IconDeferredPayment from '@/assets/icons/heroIcons/Deferred-payment.svg';
import { DeferredPaymentConditions } from '@/sections/termsAndConditions/DeferredPaymentConditions';
import { PayInThreeConditions } from '@/sections/termsAndConditions/PayInThreeConditions';
import { MiniConditions } from '@/sections/termsAndConditions/MiniConditions';
import { MaxiConditions } from '@/sections/termsAndConditions/MaxiConditions';
import { InsuranceConditions } from '@/sections/termsAndConditions/InsuranceConditions';
import { InstallmentsConditions } from '@/sections/termsAndConditions/InstallmentsConditions';
import HeroSimple from '@/sections/HeroSimple';
import { SeoHeader } from '@/components/SeoHeader';
import { Text } from '@/components/typography';
import { Button } from '@/components/buttons';
import IconShoppingCart from '@/assets/icons/heroIcons/Shopping-cart.svg';
import IconMobile from '@/assets/icons/heroIcons/Device-mobile.svg';
import IconUser from '@/assets/icons/heroIcons/User-group.svg';
import { useIsSelectedLanguage } from '@/hooks/useIsSelectedLanguage';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import { useRouter } from 'next/router';
type SelectedSectionType = 'deferredPayment' | 'payInThree' | 'mini' | 'maxi' | 'insurance' | 'installments' | null;
type SectionId = Exclude<SelectedSectionType, null>;
interface Props extends IntlProps {
  menuPromoBanners?: PageMenuPromoBanner[];
}
export default function Page(t0) {
  const $ = _c(45);
  const {
    menuPromoBanners
  } = t0;
  usePageView("termsAndConditions");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["termsAndConditions", "core"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  const router = useRouter();
  const deferredPaymentRef = useRef(null);
  const payInThreeRef = useRef(null);
  const miniRef = useRef(null);
  const maxiRef = useRef(null);
  const installmentsRef = useRef(null);
  const insuranceRef = useRef(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const isCsLanguage = useIsSelectedLanguage("cs");
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = (sectionRef, id) => {
      if (sectionRef.current) {
        setSelectedSection(id);
        setTimeout(() => {
          if (sectionRef.current) {
            sectionRef.current.scrollIntoView({
              behavior: "smooth"
            });
          }
        }, 400);
      }
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleScrollToSection = t2;
  let t3;
  if ($[2] !== selectedSection) {
    t3 = id_0 => ({
      isOpen: selectedSection === id_0,
      onOpen: () => setSelectedSection(id_0),
      onClose: () => setSelectedSection(null)
    });
    $[2] = selectedSection;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const generateToggleProps = t3;
  let t4;
  if ($[4] !== isCsLanguage) {
    t4 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const rawHash = window.location.hash.replace("#", "") as SectionId | '';
      if (!rawHash) {
        return;
      }
      const refMap = {
        deferredPayment: deferredPaymentRef,
        payInThree: payInThreeRef,
        mini: miniRef,
        maxi: maxiRef,
        installments: installmentsRef,
        insurance: insuranceRef
      };
      if (!isCsLanguage && ["deferredPayment", "maxi", "installments", "insurance"].includes(rawHash)) {
        return;
      }
      const targetRef = refMap[rawHash as SectionId];
      if (!targetRef) {
        return;
      }
      handleScrollToSection(targetRef, rawHash as SelectedSectionType);
    };
    $[4] = isCsLanguage;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== isCsLanguage || $[7] !== router.asPath) {
    t5 = [router.asPath, isCsLanguage];
    $[6] = isCsLanguage;
    $[7] = router.asPath;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== t) {
    t6 = t("seo.title");
    $[9] = t;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t) {
    t7 = t("seo.description");
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t6 || $[14] !== t7) {
    t8 = <SeoHeader title={t6} description={t7} />;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t) {
    t9 = t("banner.title");
    $[16] = t;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Button variant="unstyled" className="text-blue-500 underline decoration-1 hover:text-blue-800" testId="deferredPaymentSection" analyticsParam="deferredPaymentSection" onClick={() => handleScrollToSection(deferredPaymentRef, "deferredPayment")} />;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Button variant="unstyled" className="text-blue-500 underline decoration-1 hover:text-blue-800" testId="payInThreeSection" analyticsParam="payInThreeSection" onClick={() => handleScrollToSection(payInThreeRef, "payInThree")} />;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Button variant="unstyled" className="text-blue-500 underline decoration-1 hover:text-blue-800" testId="miniSection" analyticsParam="miniSection" onClick={() => handleScrollToSection(miniRef, "mini")} />;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Button variant="unstyled" className="text-blue-500 underline decoration-1 hover:text-blue-800" testId="maxiSection" analyticsParam="maxiSection" onClick={() => handleScrollToSection(maxiRef, "maxi")} />;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t) {
    t14 = <Text variant="p2" className="mx-auto mt-4 max-w-192 text-center text-gray-800 lg:mt-8"><Trans t={t} i18nKey="banner.text">{t10}{t11}{t12}{t13}</Trans></Text>;
    $[22] = t;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t14 || $[25] !== t9) {
    t15 = <HeroSimple title={t9}>{t14}</HeroSimple>;
    $[24] = t14;
    $[25] = t9;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== generateToggleProps || $[28] !== isCsLanguage) {
    t16 = <div className="relative z-10 mx-auto flex max-w-[75rem] flex-col gap-6 px-4 pb-20 lg:pb-40">{isCsLanguage ? <><div ref={deferredPaymentRef} className="scroll-mt-20 lg:scroll-mt-26"><DeferredPaymentConditions {...generateToggleProps("deferredPayment")} /></div><div ref={payInThreeRef} className="scroll-mt-20 lg:scroll-mt-26"><PayInThreeConditions {...generateToggleProps("payInThree")} /></div><div ref={miniRef} className="scroll-mt-20 lg:scroll-mt-26"><MiniConditions {...generateToggleProps("mini")} /></div><div ref={maxiRef} className="scroll-mt-20 lg:scroll-mt-26"><MaxiConditions {...generateToggleProps("maxi")} /></div><div ref={installmentsRef} className="scroll-mt-20 lg:scroll-mt-26"><InstallmentsConditions {...generateToggleProps("installments")} /></div><div ref={insuranceRef} className="scroll-mt-20 lg:scroll-mt-26"><InsuranceConditions {...generateToggleProps("insurance")} /></div></> : <><div ref={payInThreeRef} className="scroll-mt-20 lg:scroll-mt-26"><PayInThreeConditions {...generateToggleProps("payInThree")} /></div><div ref={miniRef} className="scroll-mt-20 lg:scroll-mt-26"><MiniConditions {...generateToggleProps("mini")} /></div></>}</div>;
    $[27] = generateToggleProps;
    $[28] = isCsLanguage;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t15 || $[31] !== t16) {
    t17 = <div className="bg-blue-50">{t15}{t16}</div>;
    $[30] = t15;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <SectionDelimiter color="bg-blue-100" background="bg-blue-50" />;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== isCsLanguage || $[35] !== t) {
    t19 = <div className="relative z-10"><MoreInfoSection buttons={isCsLanguage ? [{
        analyticsParam: "termsAndConditionsMoreInfoDeferredPayment",
        icon: <IconDeferredPayment className="fillButtonIcon" />,
        testId: "termsAndConditionsMoreInfoDeferredPayment",
        title: t("core:products.deferredPayment"),
        to: LINKS().deferredPayment
      }, {
        analyticsParam: "termsAndConditionsMoreInfoRevolvingApplication",
        icon: <IconMaxi className="fillButtonIcon" />,
        testId: "termsAndConditionsMoreInfoRevolvingApplication",
        title: t("core:products.revolving"),
        to: LINKS().revolving
      }, {
        analyticsParam: "termsAndConditionsMoreInfoPayInThree",
        icon: <IconThird className="fillButtonIcon" />,
        testId: "termsAndConditionsMoreInfoPayInThree",
        title: t("core:link.payInThree"),
        to: LINKS().payInThree
      }, {
        analyticsParam: "termsAndConditionsMoreInfoCard",
        icon: <IconCard className="fillButtonIcon" />,
        testId: "termsAndConditionsMoreInfoCard",
        title: t("core:products.card"),
        to: LINKS().card
      }] : [{
        analyticsParam: "termsAndConditionsMoreInfoPayInThree",
        icon: <IconThird className="fillButtonIcon" />,
        testId: "termsAndConditionsMoreInfoPayInThree",
        title: t("core:products.payInThree"),
        to: LINKS().payInThree
      }, {
        analyticsParam: "termsAndConditionsMoreInfoWhereToShop",
        icon: <IconShoppingCart className="fillButtonIcon" />,
        testId: "termsAndConditionsMoreInfoWhereToShop",
        title: t("core:products.whereToShop"),
        to: LINKS().whereToShop
      }, {
        analyticsParam: "termsAndConditionsMoreInfoMobileApp",
        icon: <IconMobile className="fillButtonIcon" />,
        testId: "termsAndConditionsMoreInfoMobileApp",
        title: t("core:products.mobileApp"),
        to: LINKS().mobileApp
      }, {
        analyticsParam: "termsAndConditionsMoreInfoAboutCompany",
        icon: <IconUser className="fillButtonIcon" />,
        testId: "termsAndConditionsMoreInfoAboutCompany",
        title: t("core:link.aboutUs"),
        to: LINKS().aboutCompany
      }]} /></div>;
    $[34] = isCsLanguage;
    $[35] = t;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className="bgCircle absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 rotate-180" />;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t19) {
    t21 = <div className="relative overflow-hidden bg-blue-100">{t19}{t20}</div>;
    $[38] = t19;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== menuPromoBanners || $[41] !== t17 || $[42] !== t21 || $[43] !== t8) {
    t22 = <Layout menuPromoBanners={menuPromoBanners}>{t8}{t17}{t18}{t21}</Layout>;
    $[40] = menuPromoBanners;
    $[41] = t17;
    $[42] = t21;
    $[43] = t8;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  return t22;
}
export const getStaticProps: GetStaticProps = async ctx => ({
  props: {
    menuPromoBanners: await getAllPageMenuPromoBanners(),
    ...(await getIntlProps(ctx, ['core', 'termsAndConditions']))
  }
});